/**
 * Peer Challenge Component
 * Challenge other users and compete in sales scenarios
 */

import React, { useState, useEffect } from 'react';
import { gamificationService } from '../services/gamificationService';
import { Swords, Trophy, Clock, CheckCircle, XCircle, Target } from 'lucide-react';
import { Button } from './ui/Button';

const CHALLENGE_TYPES = {
  objection_battle: {
    name: 'Objection Battle',
    description: 'Handle the same objection - best response wins',
    icon: Swords,
    color: 'text-red-600',
    bg: 'bg-red-100'
  },
  drill_race: {
    name: 'Drill Race',
    description: 'Complete 5 drills - highest average score wins',
    icon: Target,
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  },
  roleplay_duel: {
    name: 'Roleplay Duel',
    description: 'Same prospect, same scenario - who closes better?',
    icon: Trophy,
    color: 'text-purple-600',
    bg: 'bg-purple-100'
  },
  script_showdown: {
    name: 'Script Showdown',
    description: 'Pitch the same product - clarity and confidence scored',
    icon: CheckCircle,
    color: 'text-green-600',
    bg: 'bg-green-100'
  }
};

export default function PeerChallenge() {
  const [challenges, setChallenges] = useState([]);
  const [activeTab, setActiveTab] = useState('received'); // received, sent, completed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChallenges();
  }, []);

  const loadChallenges = async () => {
    try {
      const data = await gamificationService.getChallenges();
      setChallenges(data);
    } catch (error) {
      console.error('Failed to load challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (challengeId) => {
    try {
      await gamificationService.respondToChallenge(challengeId, true);
      await loadChallenges();
    } catch (error) {
      console.error('Failed to accept challenge:', error);
    }
  };

  const handleDecline = async (challengeId) => {
    try {
      await gamificationService.respondToChallenge(challengeId, false);
      await loadChallenges();
    } catch (error) {
      console.error('Failed to decline challenge:', error);
    }
  };

  const getFilteredChallenges = () => {
    if (activeTab === 'received') {
      return challenges.filter(c => c.status === 'pending' || c.status === 'accepted');
    } else if (activeTab === 'sent') {
      return challenges.filter(c => c.status === 'pending' || c.status === 'in_progress');
    } else {
      return challenges.filter(c => c.status === 'completed');
    }
  };

  const renderChallenge = (challenge) => {
    const config = CHALLENGE_TYPES[challenge.challenge_type] || CHALLENGE_TYPES.objection_battle;
    const ChallengeIcon = config.icon;

    return (
      <div
        key={challenge.id}
        className="bg-white rounded-lg border-2 border-gray-200 p-4 hover:shadow-md transition-all"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`${config.bg} ${config.color} p-2 rounded-lg`}>
              <ChallengeIcon className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">{config.name}</h4>
              <p className="text-sm text-gray-600">{config.description}</p>
            </div>
          </div>

          {challenge.status === 'completed' && (
            <div className="flex items-center gap-2">
              {challenge.winner_id ? (
                <div className="bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-full flex items-center gap-1">
                  <Trophy className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {challenge.winner_id === challenge.challenger_id ? 'Won' : 'Lost'}
                  </span>
                </div>
              ) : (
                <div className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-sm">
                  Tied
                </div>
              )}
            </div>
          )}
        </div>

        {/* Opponent info */}
        <div className="flex items-center justify-between mb-3 text-sm">
          <div>
            <span className="text-gray-600">Challenger: </span>
            <span className="font-medium text-gray-900">{challenge.challenger?.email}</span>
          </div>
          <div>
            <span className="text-gray-600">Opponent: </span>
            <span className="font-medium text-gray-900">{challenge.opponent?.email}</span>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 mb-3">
          {challenge.status === 'pending' && (
            <div className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Pending
            </div>
          )}
          {challenge.status === 'accepted' && (
            <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Accepted - Ready to start!
            </div>
          )}
          {challenge.status === 'in_progress' && (
            <div className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-medium">
              In Progress
            </div>
          )}
        </div>

        {/* Scores (if completed) */}
        {challenge.status === 'completed' && (
          <div className="grid grid-cols-2 gap-3 mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{challenge.challenger_score || 0}</div>
              <div className="text-xs text-gray-600">Challenger</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{challenge.opponent_score || 0}</div>
              <div className="text-xs text-gray-600">Opponent</div>
            </div>
          </div>
        )}

        {/* Actions */}
        {challenge.status === 'pending' && activeTab === 'received' && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => handleAccept(challenge.id)}
              className="flex-1"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDecline(challenge.id)}
              className="flex-1"
            >
              <XCircle className="w-4 h-4 mr-1" />
              Decline
            </Button>
          </div>
        )}

        {challenge.status === 'accepted' && (
          <Button size="sm" variant="default" className="w-full">
            Start Challenge
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse bg-gray-100 rounded-lg h-32" />
        ))}
      </div>
    );
  }

  const filteredChallenges = getFilteredChallenges();

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('received')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'received'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Received ({challenges.filter(c => c.status === 'pending').length})
        </button>
        <button
          onClick={() => setActiveTab('sent')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'sent'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Sent ({challenges.filter(c => c.status === 'in_progress').length})
        </button>
        <button
          onClick={() => setActiveTab('completed')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'completed'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          History ({challenges.filter(c => c.status === 'completed').length})
        </button>
      </div>

      {/* Challenges list */}
      <div className="space-y-3">
        {filteredChallenges.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Swords className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No challenges yet</h3>
            <p className="text-sm text-gray-600">
              {activeTab === 'received' && 'Waiting for others to challenge you'}
              {activeTab === 'sent' && 'Send a challenge to get started'}
              {activeTab === 'completed' && 'Complete challenges to see your history'}
            </p>
          </div>
        ) : (
          filteredChallenges.map(renderChallenge)
        )}
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-3 mt-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-700">
            {challenges.filter(c => c.status === 'completed' && c.winner_id).length}
          </div>
          <div className="text-sm text-green-600">Wins</div>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-700">
            {challenges.filter(c => c.status === 'completed' && c.winner_id && c.winner_id !== c.challenger_id).length}
          </div>
          <div className="text-sm text-red-600">Losses</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-700">
            {challenges.filter(c => c.status === 'in_progress' || c.status === 'accepted').length}
          </div>
          <div className="text-sm text-blue-600">Active</div>
        </div>
      </div>
    </div>
  );
}
